import { useState } from 'react';
import {
  hasGuidedWork,
  useGuidedEntryStore,
  type GuidedProcedure,
} from '@/stores/guidedEntryStore';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import { logger } from '@/utils/logger';

/**
 * Switching between print and embroidery, with the confirmation a switch
 * that discards work asks for first.
 */
export function useProcedureSwitch(procedure: GuidedProcedure | null) {
  const setProcedure = useGuidedEntryStore((state) => state.setProcedure);

  // The thread store is shared with the editor's own embroidery path; leaving
  // the embroidery run drops its palette, source and an open modal so nothing
  // of the old run is around later. A failure there must not block the switch.
  const applyProcedure = (next: GuidedProcedure) => {
    const current = useGuidedEntryStore.getState().procedure;
    if (next === current) return;
    if (current === 'embroidery') {
      try {
        useEmbroideryStore.getState().clearEmbroidery();
      } catch (error) {
        logger.warn(
          'GuidedDesignStep',
          'Could not clear the thread store',
          error
        );
      }
    }
    setProcedure(next);
  };

  // A switch empties both sides. With work on them the seller confirms first
  // (#367); without, it switches at once as before.
  const [pendingProcedure, setPendingProcedure] =
    useState<GuidedProcedure | null>(null);
  // The dialog's wording outlives `pendingProcedure`, so the text does not
  // flip to the other run while the dialog fades out.
  const [dialogTarget, setDialogTarget] = useState<GuidedProcedure>('print');
  const changeProcedure = (next: GuidedProcedure) => {
    if (next === procedure) return;
    if (hasGuidedWork(useGuidedEntryStore.getState())) {
      setDialogTarget(next);
      setPendingProcedure(next);
      return;
    }
    applyProcedure(next);
  };

  // Cancel, Escape and a click beside it all keep procedure and motif.
  const cancelSwitch = () => setPendingProcedure(null);
  const confirmSwitch = () => {
    if (pendingProcedure) applyProcedure(pendingProcedure);
    setPendingProcedure(null);
  };

  return {
    changeProcedure,
    switchPending: pendingProcedure !== null,
    dialogTarget,
    confirmSwitch,
    cancelSwitch,
  };
}
